import { useTranslation } from 'react-i18next';
import { ClipboardCheck } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Button } from '@/shared/components/ui/button';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/colors';
import { useMembers } from '@/features/members/public';
import { usePendingTimeOffApprovals } from '../../hooks/usePendingTimeOffApprovals';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { formatDayRange } from '../calendar/dateRangeFormat';
import { ApproveRejectActions } from './ApproveRejectActions';

/** Large enough page to resolve every requester's name/avatar for the pending list without a search UI. */
const MEMBERS_PAGE_SIZE = 200;

export function PendingApprovalsList() {
  const { t } = useTranslation('timeOff');
  const { data, isLoading, isError, refetch } = usePendingTimeOffApprovals();
  const { data: types } = useTimeOffTypes();
  const { data: members } = useMembers('', 0, MEMBERS_PAGE_SIZE);
  const typeNameById = new Map((types ?? []).map((type) => [type.id, type.name ?? '']));
  const memberByUserId = new Map((members ?? []).map((member) => [member.userId, member]));

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 p-4" data-testid="time-off-approvals-loading-skeleton">
          <Skeleton className="h-8 w-32" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent
          data-testid="time-off-approvals-error-message"
          className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
        >
          <p className="text-sm text-red-700 dark:text-red-400">{t('approvals.error')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const items = data ?? [];

  if (!items.length) {
    return (
      <div data-testid="time-off-approvals-empty-state" className="py-8 text-center">
        <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-semibold">{t('approvals.empty.title')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('approvals.empty.description')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="time-off-approvals-list">
      <h3 className="text-sm font-semibold">{t('approvals.pendingSectionTitle')}</h3>
      <Card>
        <CardContent className="divide-y p-0">
          {items.map((request) => {
            const requesterUserId = request.requesterUserId ?? '';
            const member = memberByUserId.get(requesterUserId);
            const requesterName = member?.displayName ?? requesterUserId;
            const typeName = typeNameById.get(request.typeId) ?? '';
            const activeDays = (request.days ?? []).filter((day) => day.status !== 'Cancelled');

            return (
              <div
                key={request.id}
                className="flex flex-wrap items-center justify-between gap-3 p-4"
                data-testid={`time-off-approval-item-${request.id}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <UserAvatar name={requesterName} color={getUserColor(requesterUserId)} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {requesterName} · {typeName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activeDays.length > 0 &&
                        `${formatDayRange(
                          activeDays.map((day) => day.date),
                          { includeYear: true },
                        )} · ${t('requests.table.dayCount', { count: activeDays.length })}`}
                    </p>
                  </div>
                </div>
                {request.id && (
                  <ApproveRejectActions
                    requestId={request.id}
                    requesterName={requesterName}
                    dayCount={activeDays.length}
                  />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
